const _ = require("lodash");
const {FLAGS} = require("../../util/GlobalFlags");
const diffObjects = require("../../util/diffObjects");

// Attached to actions at dispatch to track them even when other dispatch
// middleware extends or clones them. (Doesn't help if it gets completely
// replaced though.)
const dispatchInfoKey = Symbol("dispatchInfo");

let ACTION_HISTORY_LIMIT = 40;
if (process.env.NODE_ENV !== "production") {
	ACTION_HISTORY_LIMIT = Infinity;
}

/**
 * Was getting "Declaration emit for this file requires using private name".
 * This `type` annotation is a workaround. See: https://stackoverflow.com/questions/60009092/declaration-will-not-emit-due-to-private-name-usage
 * @type {unknown}
 */
const VaultLogger = function(vault) {
	if (!(this instanceof VaultLogger)) {
		return new VaultLogger(vault);
	}
	this.vault = vault;

	// Access below properties directly
	this.hasTruncatedHistory = false;
	this.actionHistory = [];
	this.lastVaultState = null;
	this.ongoingAction = null;
	// Access this one through getLastErroredAction
	this._erroredAction = null;

	setupVaultMiddleware(this);
};

VaultLogger.prototype.logSuppressedAction = function(action, reason) {
	if (process.env.NODE_ENV !== "production") {
		// eslint-disable-next-line no-console
		console.log("Action suppressed.", reason, action);
	}
	appendToHistory(this, {
		action: this.vault.updater.cleanAction(_.clone(action)),
		suppressDateNow: Date.now(),
		suppressPerfNow: getPerfNow(),
		suppressedBy: reason || "(no reason given)",
	});
};

VaultLogger.prototype.getLastErroredAction = function() {
	const e = this._erroredAction;
	this._erroredAction = null;
	return e;
};

// Private helpers

// Constructor helper. Sets up the hooks in the vault.
const setupVaultMiddleware = function(logger) {
	const vault = logger.vault;
	vault.addDispatchMiddleware(action => {
		action = _.clone(action);
		action[dispatchInfoKey] = {dateNow: Date.now(), perfNow: getPerfNow()};
		return action;
	});
	vault.addPreHandleMiddleware(action => {
		logger.ongoingAction = getBaseLoggedAction(vault, action);
		return _.omit(action, dispatchInfoKey);
	});
	vault.addPostHandleMiddleware((action, oldState, newState) => {
		logAction(
			logger,
			oldState,
			newState,
			vault.updater.getLastModifyingHooks()
		);
	});
	vault.addUpdateErrorMiddleware(() => {
		logger._erroredAction = logger.ongoingAction;
		logger.ongoingAction = null;
	});
	vault.on("update", (state, gen, events) => {
		logUpdate(logger, events);
	});
};

const getPerfNow = function() {
	return Math.floor(global.performance ? global.performance.now() : NaN);
};

// Turns diffObjects elements into strings that display better in console.
const summarizeDiff = function(d) {
	let path = d.path.reduce((acc, p) => {
		return acc + (typeof p === "number" ? "[" + p + "]" : "." + p);
	}, "state");
	if (d.kind === "A") {
		path += "[" + d.index + "]";
		d = d.item;
	}
	return d.kind + " " + path + ": " + d.lhs + " -> " + d.rhs;
};

const appendToHistory = function(logger, entry) {
	logger.actionHistory.push(entry);
	if (logger.actionHistory.length > ACTION_HISTORY_LIMIT) {
		logger.hasTruncatedHistory = true;
		logger.actionHistory.shift();
	}
};

// Gets initial entry into action history, just before it is handled.
const getBaseLoggedAction = function(vault, action) {
	const dispatchInfo = action[dispatchInfoKey] || {};
	return {
		action: vault.updater.cleanAction(_.clone(action)),
		dispatchDateNow: dispatchInfo.dateNow || null,
		dispatchPerfNow: dispatchInfo.perfNow || null,
		updateDateNow: Date.now(),
		updatePerfNow: getPerfNow(),
		emittedUpdate: false,
	};
};

// Mutates action history entry and inserts it after action is handled.
const logAction = function(logger, oldState, newState, modifyingHooks) {
	const newEntry = logger.ongoingAction;
	logger.ongoingAction = null;
	if (FLAGS.logVaultStates) {
		newEntry.preState = oldState;
		newEntry.postState = newState;
		newEntry.modifyingHooks = modifyingHooks;
		if (FLAGS.logVaultDiffs) {
			const diff = diffObjects(oldState, newState);
			newEntry.diff = diff;
			newEntry.diffSummary = diff && diff.map(summarizeDiff);
		}
	}
	appendToHistory(logger, newEntry);
	logger.lastVaultState = newState;
};

// Mutates last action history entry when vault emits an update.
const logUpdate = function(logger, events) {
	const len = logger.actionHistory.length;
	if (!len) {
		return;
	}
	logger.actionHistory[len - 1].emittedUpdate = true;
	logger.actionHistory[len - 1].emitDateNow = Date.now();
	logger.actionHistory[len - 1].emitPerfNow = getPerfNow();
	logger.actionHistory[len - 1].customEvents = events;
};

module.exports = VaultLogger;
